import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import confirmStyles from '../../components/ui/ConfirmDialog.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { useDialogDismiss } from '../../components/ui/dialogDismiss';
import type { AbsorbReason, PaidNotBilledRow } from '../../api/types';
// Same reason picker look as Reverse award (R15), so a required reason reads the same everywhere.
import styles from '../shipments/ReverseAwardDialog.module.css';
import payStyles from './Payments.module.css';
import {
  ABSORB_NOTE_MAX,
  ABSORB_REASONS,
  absorbReasonValid,
  chargeTypeLabel,
} from './absorbReason';
import { formatMoney, parsePaise } from './allocation';
import { DialogHelp } from '../../help/DialogHelp';

export interface AbsorbChoice {
  reason: AbsorbReason;
  /** Trimmed; null when blank. */
  note: string | null;
}

interface Props {
  row: PaidNotBilledRow | null;
  onConfirm: (choice: AbsorbChoice) => void;
  onCancel: () => void;
  loading?: boolean;
  /** Inline error from a refused request; the picked reason and note stay as they were. */
  errorMessage?: string | null;
}

/** Absorb a paid partner charge: DNM keeps the cost and never bills it. A reason is required; Other needs a note. */
export function AbsorbChargeDialog({ row, onConfirm, onCancel, loading, errorMessage }: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState<AbsorbReason | null>(null);
  const [note, setNote] = useState('');
  const titleId = useId();
  const noteId = useId();
  const dismiss = useDialogDismiss({
    kind: 'form',
    dirty: code !== null || note.trim() !== '',
    busy: loading,
    onClose: onCancel,
  });
  const chargeId = row?.chargeId;

  useEffect(() => {
    setCode(null);
    setNote('');
  }, [chargeId]);

  if (!row) return null;

  const sentNote = code === 'OTHER' ? note : '';
  const valid = absorbReasonValid(code, sentNote);
  const trimmed = sentNote.trim();
  const amount = formatMoney(parsePaise(row.partnerAmount));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !code || loading) return;
    onConfirm({ reason: code, note: trimmed || null });
  };

  return (
    <DialogShell
      dismiss={dismiss}
      labelledBy={titleId}
      scrimClassName={confirmStyles.backdrop}
      panelClassName={confirmStyles.modal}
    >
      <div className={confirmStyles.header}>
        <h3 id={titleId} className={confirmStyles.title}>
          {t('payments.absorbChargeDialog.title', { amount })}
        </h3>
        <div className={payStyles.headerTools}>
          <DialogHelp context="absorb-charge" title={t('payments.absorbChargeDialog.title', { amount })} />
          <button
            type="button"
            className={confirmStyles.closeBtn}
            onClick={dismiss.requestClose}
            disabled={loading}
            aria-label={t('common.actions.close')}
          >
            &times;
          </button>
        </div>
      </div>
      <form onSubmit={handleSubmit} className={confirmStyles.body}>
        <div className={confirmStyles.description}>
          {t('payments.absorbChargeDialog.description', {
            charge: chargeTypeLabel(row.chargeType),
            shipmentId: row.shipmentId,
            partner: row.partnerName,
            shipper: row.shipperName,
          })}
        </div>

        <fieldset className={styles.choices} disabled={loading}>
          <legend className={styles.legend}>{t('payments.absorbChargeDialog.reason')}</legend>
          {ABSORB_REASONS.map((r, i) => (
            <label
              key={r.code}
              className={`${styles.choice} ${code === r.code ? styles.choiceOn : ''}`}
            >
              <input
                type="radio"
                name="absorb-reason"
                value={r.code}
                checked={code === r.code}
                onChange={() => setCode(r.code)}
                className={styles.radio}
                data-autofocus={i === 0 ? true : undefined}
              />
              <span className={styles.icon}>
                <StatusIcon name={r.icon} size={20} />
              </span>
              <span className={styles.text}>
                <span className={styles.label}>{r.label}</span>
                <span className={styles.hint}>{r.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {code === 'OTHER' && (
          <div className={confirmStyles.reasonWrapper}>
            <label htmlFor={noteId} className={styles.noteLabel}>
              {t('payments.absorbChargeDialog.note')}
            </label>
            <textarea
              id={noteId}
              className={confirmStyles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={ABSORB_NOTE_MAX}
              rows={3}
              disabled={loading}
              placeholder={t('payments.absorbChargeDialog.notePlaceholder')}
            />
            <span
              className={`${styles.counter} ${trimmed.length > ABSORB_NOTE_MAX ? styles.counterOver : ''}`}
            >
              {trimmed.length}/{ABSORB_NOTE_MAX}
            </span>
          </div>
        )}

        {errorMessage && (
          <span role="alert" className={confirmStyles.errorText}>
            {errorMessage}
          </span>
        )}

        <div className={confirmStyles.actions}>
          <Button variant="secondary" onClick={dismiss.requestClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button variant="danger" type="submit" disabled={loading || !valid} busy={loading}>
            {loading ? 'Absorbing…' : 'Absorb charge'}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
